import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Response200Type as PromotedEvents200Response } from 'src/api_openapi/generated/openapi_website_cms_getAllPromotedSports';
import { Response200Type as TopEvents200Response } from 'src/api_openapi/generated/openapi_website_cms_getAllTopEventsSports';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { PaginationConfigType } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { DateRange } from 'react-day-picker';

export type SportEventModelType = PromotedEvents200Response[number] | TopEvents200Response[number];
type BulkDeleteResponseType = { status: 200; body: number } | { status: 400 | 401 | 404 | 500; body?: unknown };

type EventTableRowDataType = {
    id: number;
    eventId: string;
    name: string;
    sport: string;
    competition: string;
    startTime: string;
};

const pageSizeOptions: number[] = [5];

interface SelectValueType {
    value: string;
    label: string;
    onClick: () => void;
}

export class BulkDeleteDialogState<TEvent extends SportEventModelType> {
    @observable public readonly eventsResource: Resource<Array<TEvent>>;
    @observable public selectedEventIds: Set<number> = new Set();
    @observable private sortField: keyof EventTableRowDataType = 'id';
    @observable private sortDirection: 'asc' | 'desc' = 'desc';
    @observable public selectedDate: DateRange | null = null;
    @observable public isOpen: boolean = false;
    @observable public isSuccessDialogOpen: boolean = false;
    @observable public isErrorDialogOpen: boolean = false;

    private currentPage: number = 1;
    private rowsPerPage: number = 5;
    private rowsPerPageState = FormInputState.new('');

    public constructor(
        private readonly modelsState: ModelsState,
        private readonly onBulkDelete: (payload: { ids: number[] }) => Promise<BulkDeleteResponseType>,
        eventsResource: Resource<Array<TEvent>>
    ) {
        this.eventsResource = eventsResource;
        makeAutoObservable(this);
    }

    @computed private get eventsData(): Array<TEvent> | null {
        const eventsData = this.eventsResource.get();
        if (eventsData.type === 'ready') {
            return eventsData.value;
        }
        return null;
    }

    @computed
    public get data(): EventTableRowDataType[] {
        if (this.eventsData === null) {
            return [];
        }

        const data = [...this.eventsData];
        const returnList: EventTableRowDataType[] = [];

        for (const entry of data) {
            for (const competition of entry.competitions_list) {
                for (const event of competition.events_list) {
                    const eventModel = this.modelsState.getEvent(event.event_id);
                    if (eventModel !== null) {
                        returnList.push({
                            id: event.id,
                            eventId: eventModel.id2.rawId,
                            name: eventModel.name,
                            sport: eventModel.sport,
                            competition: eventModel.competition2.getModel()?.name ?? '',
                            startTime: eventModel.timeSettingsStartTime,
                        });
                    }
                }
            }
        }

        return returnList;
    }

    @action public setSortConfig = (field: keyof EventTableRowDataType): void => {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDirection = 'asc';
        }
    };

    @action public setSelectedDate = (date: DateRange | undefined): void => {
        this.selectedDate = date ?? null;
        this.currentPage = 1;
        this.deselectAllEvents();
    };

    private startOfDay = (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    };

    private endOfDay = (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
    };

    @computed private get filteredData(): EventTableRowDataType[] {
        let data = [...this.data];

        if (this.selectedDate !== null) {
            const dateFrom = this.selectedDate.from;
            const dateTo = this.selectedDate.to;
            if (dateFrom !== undefined && dateTo !== undefined) {
                const dateFromTime = this.startOfDay(dateFrom).getTime();
                const dateToTime = this.endOfDay(dateTo).getTime();
                data = data.filter((event) => {
                    const eventDateTime = new Date(event.startTime).getTime();
                    return dateFromTime <= eventDateTime && eventDateTime <= dateToTime;
                });
            }
        }

        data.sort((a, b) => {
            const aValue = a[this.sortField];
            const bValue = b[this.sortField];

            let comparison = 0;
            if (aValue < bValue) {
                comparison = -1;
            } else if (aValue > bValue) {
                comparison = 1;
            }

            return this.sortDirection === 'asc' ? comparison : -comparison;
        });

        return data;
    }

    @computed public get paginatedData(): EventTableRowDataType[] {
        const data = this.filteredData;

        const startIndex = (this.currentPage - 1) * this.rowsPerPage;
        return data.slice(startIndex, startIndex + this.rowsPerPage);
    }

    @computed private get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage));
    }

    @computed private get totalItems(): number {
        return this.filteredData.length;
    }

    @computed private get startItem(): number {
        if (this.totalItems === 0) {
            return 0;
        }
        return (this.currentPage - 1) * this.rowsPerPage + 1;
    }

    @computed private get endItem(): number {
        return Math.min(this.startItem + this.rowsPerPage - 1, this.totalItems);
    }

    @action private setPage = (page: number): void => {
        this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    };

    @action private resetPagination = (): void => {
        this.setPage(1);
    };

    @action private setRowsPerPage = (rows: number): void => {
        this.rowsPerPage = rows;
        this.rowsPerPageState.setValue(rows.toString());
        this.currentPage = 1;
    };

    private get selectValues(): SelectValueType[] {
        return pageSizeOptions.map((size) => ({
            value: size.toString(),
            label: size.toString(),
            onClick: () => this.setRowsPerPage(size),
        }));
    }

    @computed public get paginationConfig(): PaginationConfigType<string> {
        return {
            currentPage: this.currentPage,
            endItem: this.endItem,
            setPage: this.setPage,
            startItem: this.startItem,
            totalItems: this.totalItems,
            totalPages: this.totalPages,
            rowsPerPageState: this.rowsPerPageState,
            selectValues: this.selectValues,
            fixedPageSize: this.rowsPerPage,
        };
    }

    @computed private get isAllSelected(): boolean {
        return this.filteredData.length > 0 && this.filteredData.every((event) => this.selectedEventIds.has(event.id));
    }

    @computed public get isIndeterminate(): boolean {
        return this.selectedEventIds.size > 0 && !this.isAllSelected;
    }

    @computed public get isHeaderChecked(): boolean {
        return this.isAllSelected;
    }

    @action public toggleEventSelection = (eventId: number): void => {
        if (this.selectedEventIds.has(eventId)) {
            this.selectedEventIds.delete(eventId);
        } else {
            this.selectedEventIds.add(eventId);
        }
    };

    @action private selectAllEvents = (): void => {
        this.filteredData.forEach((event) => {
            this.selectedEventIds.add(event.id);
        });
    };

    @action private deselectAllEvents = (): void => {
        this.selectedEventIds.clear();
    };

    @action public handleSelectAll = (checked: boolean): void => {
        if (checked) {
            this.selectAllEvents();
        } else {
            this.deselectAllEvents();
        }
    };

    public isEventSelected = (eventId: number): boolean => {
        return this.selectedEventIds.has(eventId);
    };

    @action private handleBulkDelete = async (): Promise<boolean> => {
        try {
            const selectedEventIds = Array.from(this.selectedEventIds);

            if (selectedEventIds.length === 0) {
                return false;
            }

            const deleteResult = await this.onBulkDelete({ ids: selectedEventIds });

            if (deleteResult.status === 200) {
                console.log(`Successfully deleted ${deleteResult.body} events`);
                await this.eventsResource.refresh();
                this.deselectAllEvents();
                return true;
            } else {
                console.error(`Failed to bulk delete events. Status: ${deleteResult.status}`);
                return false;
            }
        } catch (error) {
            console.error('Error during bulk delete:', error);
            return false;
        }
    };

    @action public openDialog = (): void => {
        this.isOpen = true;
    };

    @action public closeDialog = (): void => {
        this.isOpen = false;
        this.deselectAllEvents();
        this.sortField = 'id';
        this.sortDirection = 'desc';
        this.resetPagination();
    };

    @action private showSuccessDialog = (): void => {
        setTimeout(() => {
            this.isSuccessDialogOpen = true;
        }, 250);
    };

    @action private showErrorDialog = (): void => {
        setTimeout(() => {
            this.isErrorDialogOpen = true;
        }, 250);
    };

    @action public hideSuccessDialog = (): void => {
        this.isSuccessDialogOpen = false;
    };

    @action public hideErrorDialog = (): void => {
        this.isErrorDialogOpen = false;
    };

    @action public handleBulkDeleteWithDialog = async (): Promise<void> => {
        const isSuccess = await this.handleBulkDelete();
        if (isSuccess) {
            this.showSuccessDialog();
        } else {
            this.showErrorDialog();
        }
    };
}
